// Task Template Form
// Create and edit task templates with multi-protocol support

import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, MoveUp, MoveDown } from 'lucide-react';
import { createDefaultTaskTemplate, createProtocolReference, createEscalationRule, validateTaskTemplate } from '../../../types/taskTemplate';
import { useTaskTemplateMutations } from '../../../hooks/useTaskTemplates';
import { useProtocols } from '../../../hooks/useProtocols';
import { useUserGroups } from '../../../hooks/useUserGroups';
import { useActiveRoles } from '../../../hooks/useRoles';
import { userService } from '../../../services/firestoreService';
import {
  useTaskTypes,
  useTaskPriorities,
  useTaskUrgencies,
  useAssignmentTypes,
  usePatientConditions,
  useIsolationLevels,
  useEquipmentTypesTask,
  useProcedureTypes,
  useEnvironmentalRisks,
  useShiftTypes
} from '../../../hooks/useTaskNomenclatures';
import './TaskTemplateForm.css';

const TaskTemplateForm = ({ template, userId, onSave, onCancel }) => {
  const [formData, setFormData] = useState(template || createDefaultTaskTemplate());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  const { createTemplate, updateTemplate } = useTaskTemplateMutations();
  const { protocols } = useProtocols();
  const { groups, loading: groupsLoading } = useUserGroups({ isActive: true });
  const { roles } = useActiveRoles();

  // Load users for user-based assignment
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await userService.getAll();
        setUsers(usersData || []);
      } catch (error) {
        console.error('Error loading users:', error);
        setUsers([]);
      }
    };
    loadUsers();
  }, []);
  
  // Load all nomenclatures
  const { items: taskTypes } = useTaskTypes();
  const { items: priorities } = useTaskPriorities();
  const { items: urgencies } = useTaskUrgencies();
  const { items: assignmentTypes } = useAssignmentTypes();
  const { items: patientConditions } = usePatientConditions();
  const { items: isolationLevels } = useIsolationLevels();
  const { items: equipmentTypes } = useEquipmentTypesTask();
  const { items: procedureTypes } = useProcedureTypes();
  const { items: environmentalRisks } = useEnvironmentalRisks();
  const { items: shiftTypes } = useShiftTypes();

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // ==================== PROTOCOL MANAGEMENT ====================

  const handleAddProtocol = () => {
    const newProtocol = createProtocolReference('', '', formData.protocols.length + 1);
    setFormData(prev => ({
      ...prev,
      protocols: [...prev.protocols, newProtocol]
    }));
  };

  const handleRemoveProtocol = (index) => {
    setFormData(prev => ({
      ...prev,
      protocols: prev.protocols.filter((_, i) => i !== index)
    }));
  };

  const handleProtocolChange = (index, field, value) => {
    setFormData(prev => {
      const updatedProtocols = [...prev.protocols];
      updatedProtocols[index] = {
        ...updatedProtocols[index],
        [field]: value
      };
      
      // If protocol ID changed, update protocol name
      if (field === 'protocolId' && protocols) {
        const selectedProtocol = protocols.find(p => p.id === value);
        if (selectedProtocol) {
          updatedProtocols[index].protocolName = selectedProtocol.name;
        }
      }
      
      return {
        ...prev,
        protocols: updatedProtocols
      };
    });
  };

  const moveProtocol = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.protocols.length) return;

    setFormData(prev => {
      const updatedProtocols = [...prev.protocols];
      [updatedProtocols[index], updatedProtocols[newIndex]] = [updatedProtocols[newIndex], updatedProtocols[index]];
      
      // Update sequence orders
      updatedProtocols.forEach((p, i) => {
        p.sequenceOrder = i + 1;
      });
      
      return {
        ...prev,
        protocols: updatedProtocols
      };
    });
  };

  // ==================== ESCALATION RULES ====================

  const handleAddEscalationRule = () => {
    const newRule = createEscalationRule('', '', [], 30);
    setFormData(prev => ({
      ...prev,
      escalationRules: [...prev.escalationRules, newRule]
    }));
  };

  const handleRemoveEscalationRule = (index) => {
    setFormData(prev => ({
      ...prev,
      escalationRules: prev.escalationRules.filter((_, i) => i !== index)
    }));
  };

  const handleEscalationRuleChange = (index, field, value) => {
    setFormData(prev => {
      const updatedRules = [...prev.escalationRules];
      updatedRules[index] = {
        ...updatedRules[index],
        [field]: value
      };
      return {
        ...prev,
        escalationRules: updatedRules
      };
    });
  };

  // ==================== FORM SUBMISSION ====================

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const validation = validateTaskTemplate(formData);
    if (!validation.valid) {
      const errorObj = {};
      validation.errors.forEach(err => {
        errorObj.general = (errorObj.general || '') + err + '. ';
      });
      setErrors(errorObj);
      return;
    }

    try {
      setSaving(true);
      setErrors({});

      if (template && template.id) {
        // Update existing template
        await updateTemplate(template.id, formData, userId);
      } else {
        // Create new template
        await createTemplate(formData, userId);
      }

      onSave();
    } catch (error) {
      setErrors({ general: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="task-template-form">
      <div className="form-header">
        <div>
          <h2>{template ? 'Edit Task Template' : 'Create Task Template'}</h2>
          <p className="subtitle">Define a reusable task template with protocols and rules</p>
        </div>
        <button className="btn btn-secondary" onClick={onCancel}>
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="template-form-content">
        {errors.general && (
          <div className="error-message">{errors.general}</div>
        )}

        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Template Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Terminal Cleaning"
                required
              />
            </div>

            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe what this template is used for..."
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Task Type *</label>
              <select
                value={formData.taskType}
                onChange={(e) => handleChange('taskType', e.target.value)}
                required
              >
                <option value="">Select Task Type</option>
                {taskTypes && taskTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Default Priority</label>
              <select
                value={formData.defaultPriority}
                onChange={(e) => handleChange('defaultPriority', e.target.value)}
              >
                {priorities && priorities.map(priority => (
                  <option key={priority.id} value={priority.id}>{priority.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Default Urgency</label>
              <select
                value={formData.defaultUrgency}
                onChange={(e) => handleChange('defaultUrgency', e.target.value)}
              >
                {urgencies && urgencies.map(urgency => (
                  <option key={urgency.id} value={urgency.id}>{urgency.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.isActive ? 'active' : 'inactive'}
                onChange={(e) => handleChange('isActive', e.target.value === 'active')}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Protocols */}
        <div className="form-section">
          <div className="section-header">
            <h3>Protocols *</h3>
            <button type="button" className="btn btn-secondary" onClick={handleAddProtocol}>
              <Plus size={16} /> Add Protocol
            </button>
          </div>

          {formData.protocols.length === 0 ? (
            <div className="empty-hint">
              No protocols added. Click "Add Protocol" to include protocols in this template.
            </div>
          ) : (
            <div className="protocols-list-editor">
              {formData.protocols.map((protocol, index) => (
                <div key={index} className="protocol-item">
                  <div className="protocol-item-header">
                    <span className="protocol-order">#{protocol.sequenceOrder}</span>
                    <div className="protocol-actions">
                      <button
                        type="button"
                        className="move-btn"
                        onClick={() => moveProtocol(index, 'up')}
                        disabled={index === 0}
                        title="Move Up"
                      >
                        <MoveUp size={14} />
                      </button>
                      <button
                        type="button"
                        className="move-btn"
                        onClick={() => moveProtocol(index, 'down')}
                        disabled={index === formData.protocols.length - 1}
                        title="Move Down"
                      >
                        <MoveDown size={14} />
                      </button>
                      <button
                        type="button"
                        className="delete-btn-small"
                        onClick={() => handleRemoveProtocol(index)}
                        title="Remove Protocol"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="protocol-fields">
                    <div className="form-group">
                      <label>Protocol *</label>
                      <select
                        value={protocol.protocolId}
                        onChange={(e) => handleProtocolChange(index, 'protocolId', e.target.value)}
                        required
                      >
                        <option value="">Select Protocol</option>
                        {protocols && protocols.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={protocol.isRequired}
                          onChange={(e) => handleProtocolChange(index, 'isRequired', e.target.checked)}
                        />
                        {' '}Required
                      </label>
                    </div>

                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={protocol.canSkip}
                          onChange={(e) => handleProtocolChange(index, 'canSkip', e.target.checked)}
                        />
                        {' '}Can Skip
                      </label>
                    </div>

                    {protocol.canSkip && (
                      <div className="form-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={protocol.skipRequiresApproval}
                            onChange={(e) => handleProtocolChange(index, 'skipRequiresApproval', e.target.checked)}
                          />
                          {' '}Skip Requires Approval
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assignment Rules */}
        <div className="form-section">
          <h3>Assignment Rules</h3>
          
          <div className="form-grid">
            <div className="form-group">
              <label>Assignment Type *</label>
              <select
                value={formData.assignmentRules.assignmentType}
                onChange={(e) => handleNestedChange('assignmentRules', 'assignmentType', e.target.value)}
                required
              >
                {assignmentTypes && assignmentTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>

            {/* Context-Specific Assignment Fields */}
            {(() => {
              const selectedType = assignmentTypes?.find(t => t.id === formData.assignmentRules.assignmentType);
              const typeName = selectedType?.name?.toLowerCase() || '';
              
              // Group-Based Assignment
              if (typeName.includes('group')) {
                return (
                  <div className="form-group" key="group-assignment">
                    <label>Assigned to Group *</label>
                    <select
                      value={formData.assignmentRules.assignedToGroup || ''}
                      onChange={(e) => handleNestedChange('assignmentRules', 'assignedToGroup', e.target.value)}
                      required
                    >
                      <option value="">Select Group</option>
                      {groupsLoading ? (
                        <option disabled>Loading groups...</option>
                      ) : groups && groups.length > 0 ? (
                        groups.map(group => (
                          <option key={group.id} value={group.id}>{group.name || group.id}</option>
                        ))
                      ) : (
                        <option disabled>No groups available. Create groups in User Group Management first.</option>
                      )}
                    </select>
                    <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
                      Task will be assigned to available group member
                    </small>
                  </div>
                );
              }
              
              // Role-Based Assignment
              if (typeName.includes('role')) {
                return (
                  <div className="form-group" key="role-assignment">
                    <label>Required Roles *</label>
                    <select
                      multiple
                      value={formData.assignmentRules.requiredRoles || []}
                      onChange={(e) => {
                        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                        handleNestedChange('assignmentRules', 'requiredRoles', selectedOptions);
                      }}
                      size={Math.min(roles.length || 3, 6)}
                      style={{ height: 'auto', minHeight: '100px' }}
                      required
                    >
                      {roles && roles.length > 0 ? (
                        roles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))
                      ) : (
                        <option disabled>No roles available</option>
                      )}
                    </select>
                    <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
                      Hold Ctrl/Cmd to select multiple roles. Task will be assigned to any user with these roles.
                    </small>
                  </div>
                );
              }
              
              // Shift-Based Assignment
              if (typeName.includes('shift')) {
                return (
                  <div className="form-group" key="shift-assignment">
                    <label>Shift Type *</label>
                    <select
                      value={formData.assignmentRules.shiftType || ''}
                      onChange={(e) => handleNestedChange('assignmentRules', 'shiftType', e.target.value)}
                      required
                    >
                      <option value="">Select Shift</option>
                      {shiftTypes && shiftTypes.map(shift => (
                        <option key={shift.id} value={shift.id}>{shift.name}</option>
                      ))}
                    </select>
                    <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
                      Task will be assigned to shift team
                    </small>
                  </div>
                );
              }
              
              // User-Based/Direct Assignment
              if (typeName.includes('user') || typeName.includes('direct')) {
                return (
                  <div className="form-group" key="user-direct-assignment">
                    <label>Assign to User {typeName.includes('direct') ? '(Optional - can be selected during generation)' : '*'}</label>
                    <select
                      value={formData.assignmentRules.assignedToUser || ''}
                      onChange={(e) => handleNestedChange('assignmentRules', 'assignedToUser', e.target.value)}
                      required={!typeName.includes('direct')}
                    >
                      <option value="">Select User</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name && user.last_name 
                            ? `${user.first_name} ${user.last_name} (${user.email || user.id})`
                            : user.email || user.id}
                        </option>
                      ))}
                    </select>
                    <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
                      {typeName.includes('direct') 
                        ? 'Leave empty to assign during task generation. If set, this user will be pre-assigned.'
                        : 'Task will be assigned to this specific user'}
                    </small>
                  </div>
                );
              }

              // Department Pool Assignment
              if (typeName.includes('department')) {
                return (
                  <div className="form-group" key="dept-assignment">
                    <label>Assignment Notes</label>
                    <textarea
                      value={formData.assignmentRules.assignmentNotes || ''}
                      onChange={(e) => handleNestedChange('assignmentRules', 'assignmentNotes', e.target.value)}
                      rows={2}
                      placeholder="Task will be assigned to first available person in department..."
                    />
                    <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
                      First available person in the department will be assigned
                    </small>
                  </div>
                );
              }
              
              // Skill-Based or Emergency Assignment
              if (typeName.includes('skill') || typeName.includes('emergency')) {
                return (
                  <div className="form-group" key="skill-emergency-assignment">
                    <label>Special Requirements</label>
                    <textarea
                      value={formData.assignmentRules.specialRequirements || ''}
                      onChange={(e) => handleNestedChange('assignmentRules', 'specialRequirements', e.target.value)}
                      rows={2}
                      placeholder={typeName.includes('skill') 
                        ? 'Describe required skills or competencies...' 
                        : 'Emergency assignment criteria...'}
                    />
                    <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '0.75rem' }}>
                      {typeName.includes('skill') 
                        ? 'Specific competency or certification requirements' 
                        : 'Override normal assignment rules in emergencies'}
                    </small>
                  </div>
                );
              }
              
              return null;
            })()}

            <div className="form-group">
              <label>Preferred Shift</label>
              <select
                value={formData.assignmentRules.preferredShift || ''}
                onChange={(e) => handleNestedChange('assignmentRules', 'preferredShift', e.target.value)}
              >
                <option value="">Any Shift</option>
                {shiftTypes && shiftTypes.map(shift => (
                  <option key={shift.id} value={shift.id}>{shift.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Max Concurrent Tasks</label>
              <input
                type="number"
                value={formData.assignmentRules.workloadThreshold}
                onChange={(e) => handleNestedChange('assignmentRules', 'workloadThreshold', parseInt(e.target.value))}
                min="1"
              />
            </div>
          </div>
        </div>

        {/* Timing Rules */}
        <div className="form-section">
          <h3>Timing Rules</h3>
          
          <div className="form-grid">
            <div className="form-group">
              <label>Estimated Duration *</label>
              <input
                type="number"
                value={formData.timingRules.estimatedDuration}
                onChange={(e) => handleNestedChange('timingRules', 'estimatedDuration', parseInt(e.target.value))}
                min="1"
                required
              />
            </div>

            <div className="form-group">
              <label>Duration Unit</label>
              <select
                value={formData.timingRules.durationUnit}
                onChange={(e) => handleNestedChange('timingRules', 'durationUnit', e.target.value)}
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
              </select>
            </div>

            <div className="form-group">
              <label>Must Start Within</label>
              <input
                type="number"
                value={formData.timingRules.mustStartWithin || ''}
                onChange={(e) => handleNestedChange('timingRules', 'mustStartWithin', e.target.value ? parseInt(e.target.value) : null)}
                min="1"
                placeholder="Optional"
              />
            </div>

            <div className="form-group">
              <label>Must Complete Within</label>
              <input
                type="number"
                value={formData.timingRules.mustCompleteWithin || ''}
                onChange={(e) => handleNestedChange('timingRules', 'mustCompleteWithin', e.target.value ? parseInt(e.target.value) : null)}
                min="1"
                placeholder="Optional"
              />
            </div>

            <div className="form-group">
              <label>Time Unit</label>
              <select
                value={formData.timingRules.timeUnit}
                onChange={(e) => handleNestedChange('timingRules', 'timeUnit', e.target.value)}
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.timingRules.allowExtension}
                  onChange={(e) => handleNestedChange('timingRules', 'allowExtension', e.target.checked)}
                />
                {' '}Allow Extension
              </label>
            </div>

            {formData.timingRules.allowExtension && (
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.timingRules.extensionApprovalRequired}
                    onChange={(e) => handleNestedChange('timingRules', 'extensionApprovalRequired', e.target.checked)}
                  />
                  {' '}Extension Requires Approval
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Default Context */}
        <div className="form-section">
          <h3>Default Context</h3>
          
          <div className="form-grid">
            <div className="form-group">
              <label>Patient Condition</label>
              <select
                value={formData.defaultContext.patientCondition || ''}
                onChange={(e) => handleNestedChange('defaultContext', 'patientCondition', e.target.value)}
              >
                <option value="">Not Specified</option>
                {patientConditions && patientConditions.map(condition => (
                  <option key={condition.id} value={condition.id}>{condition.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Isolation Level</label>
              <select
                value={formData.defaultContext.isolationLevel || ''}
                onChange={(e) => handleNestedChange('defaultContext', 'isolationLevel', e.target.value)}
              >
                <option value="">Not Specified</option>
                {isolationLevels && isolationLevels.map(level => (
                  <option key={level.id} value={level.id}>{level.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Procedure Type</label>
              <select
                value={formData.defaultContext.procedureType || ''}
                onChange={(e) => handleNestedChange('defaultContext', 'procedureType', e.target.value)}
              >
                <option value="">Not Specified</option>
                {procedureTypes && procedureTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Environmental Risk</label>
              <select
                value={formData.defaultContext.environmentalRisk || ''}
                onChange={(e) => handleNestedChange('defaultContext', 'environmentalRisk', e.target.value)}
              >
                <option value="">Not Specified</option>
                {environmentalRisks && environmentalRisks.map(risk => (
                  <option key={risk.id} value={risk.id}>{risk.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label>Context Notes</label>
              <textarea
                value={formData.defaultContext.contextNotes}
                onChange={(e) => handleNestedChange('defaultContext', 'contextNotes', e.target.value)}
                placeholder="Additional context information..."
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="button" className="btn btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn btn-save" disabled={saving}>
            {saving ? 'Saving...' : (template ? 'Update Template' : 'Create Template')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TaskTemplateForm;

